import React, { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  addDoc,
  updateDoc,
  writeBatch,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  average
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useAppSelector } from '../redux/store';
import getCurrentScreen from '../Helper/GetScreen';

interface LeadStage {
  id: string;
  stage: string;
  color: string;
  priority: number;
}

const LeadByStage = () => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const [leadStages, setLeadStages] = useState<LeadStage[]>([]);
  const [stageCounts, setStageCounts] = useState<{ [key: string]: number }>({});
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanyId = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          setCompanyId(userDocSnap.data().companyId);
        }
      }
    };
    fetchCompanyId();
  }, []);

  useEffect(() => {
    const fetchStagesAndCounts = async () => {
      if (!companyId) return;

      try {
        const stagesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`));
        const fetchedStages = stagesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          } as LeadStage))
          .sort((a, b) => a.priority - b.priority);

        setLeadStages(fetchedStages);

        const leadsRef = collection(db, `companies/${companyId}/leads`);
        const countPromises = fetchedStages.map(async (stage) => {
          let stageQuery = query(leadsRef, where('stageId', '==', stage.id));

          if (isAdmin && currentScreen === 'Admin') {
            stageQuery = query(leadsRef, where('statusId', '==', stage.id));
          } else if (isManager && currentScreen === 'Manager') {
            const usersQuery = query(
              collection(db, 'users'),
              where('companyId', '==', userData?.companyId),
              where('managerId', '==', currentUser?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);
            stageQuery = query(leadsRef, where('statusId', '==', stage.id), where('assignedTo', 'in', users));
          } else if (isStaff && currentScreen === 'Staff') {
            stageQuery = query(leadsRef, where('statusId', '==', stage.id), where('assignedTo', '==', currentUser?.uid));
          }

          const countSnapshot = await getCountFromServer(stageQuery);
          return {
            stageId: stage.id,
            count: countSnapshot.data().count
          };
        });

        const stageCountResults = await Promise.all(countPromises);

        const counts: { [key: string]: number } = {};
        stageCountResults.forEach(({ stageId, count }) => {
          counts[stageId] = count;
        });

        setStageCounts(counts);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchStagesAndCounts();
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div
          className="flex items-center justify-center w-16 h-16 rounded-full"
          style={{
            background: "linear-gradient(180deg, #FFA782 0%, #FC583D 100%)",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/step.svg`}
            alt="Icon"
            className="w-10 h-10"
          />
        </div>
        <h2
          className="text-2xl text-black font-medium"
          style={{
            fontFamily: "Montserrat",
            lineHeight: "29.26px",
          }}
        >
          Lead By Stage
        </h2>
      </div>

      <div
        className="overflow-y-auto max-h-[17rem] rounded-2xl"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#82b3c2  #DAEBF3" }} >
        <div className="grid grid-cols-1 gap-4 mb-6">
          {leadStages.map((stage) => (
            <div
              key={stage.id}
              className="flex items-center bg-zinc-100 relative p-4 rounded-lg"
              style={{
                borderRadius: "32px 32px 32px 0",
                borderColor: stage.color || '#652636',
                borderWidth: "2px",
              }}
            >
              <div className="flex items-center justify-between w-full px-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{stage.stage}</span>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="text-lg ml-8">
                    {stageCounts[stage.id] || 0}
                  </div>
                  <div className="flex items-center justify-center w-10 h-10 bg-white rounded-full">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/desktop/iconamoon_arrow-top.svg`}
                      alt="Status"
                      className="w-6 h-6"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadByStage;
